/**
 * Query Planner: takes a parsed AST + mapping registry and produces an ExecutionPlan.
 *
 * The planner decides:
 *  - Whether to use the ORM Facility API, ORM Milestone API, or Overpass
 *  - Which Overpass QL query to generate
 *  - When a query is only partially translatable (partial-translation) or fully unsupported
 *
 * The planner contains NO network logic. It only produces plan objects.
 */

import type { Query, FilterExpr, EraClass } from '../ast/index.js';
import type { MappingRule, UnmappedRule } from '../ast/registry-types.js';

// ---------------------------------------------------------------------------
// Execution plan types
// ---------------------------------------------------------------------------

export interface OrmFacilityLookup {
  kind: 'orm-facility-lookup';
  /** Search term (name, uic, or ref) */
  searchTerm: string;
  searchType: 'name' | 'uic' | 'ref';
  limit: number;
  mappingRule: MappingRule;
}

export interface OrmMilestoneLookup {
  kind: 'orm-milestone-lookup';
  lineRef: string;
  km: number;
  mappingRule: MappingRule;
}

export interface OverpassPrimary {
  kind: 'overpass-primary';
  overpassQL: string;
  eraClass: EraClass;
  mappingRule: MappingRule;
  postFilters: PostFilter[];
}

export interface OverpassPlusPostFilter {
  kind: 'overpass-plus-postfilter';
  overpassQL: string;
  eraClass: EraClass;
  mappingRule: MappingRule;
  postFilters: PostFilter[];
  warnings: string[];
}

export interface PartialTranslation {
  kind: 'partial-translation';
  /** The best available plan for the translatable parts */
  basePlan: OverpassPrimary | OrmFacilityLookup | null;
  unmappedFields: string[];
  warnings: string[];
}

export interface UnsupportedPlan {
  kind: 'unsupported';
  reason: string;
}

export type ExecutionPlan =
  | OrmFacilityLookup
  | OrmMilestoneLookup
  | OverpassPrimary
  | OverpassPlusPostFilter
  | PartialTranslation
  | UnsupportedPlan;

/** A post-filter to apply to Overpass results in-memory */
export interface PostFilter {
  description: string;
  /** Tags that must be absent for the result to be included */
  excludeTagValues?: Record<string, string[]>;
  /** Tags that must be present */
  requireTagValues?: Record<string, string[]>;
}

// ---------------------------------------------------------------------------
// Overpass QL builder helpers
// ---------------------------------------------------------------------------

const OVERPASS_HEADER = '[out:json][timeout:25][maxsize:104857600];\n';

function eraClassToOsmFilter(_cls: EraClass, rule: MappingRule): string {
  const tagEntries = Object.entries(rule.osmTags);
  if (tagEntries.length === 0) return '';
  return tagEntries.map(([k, v]) => {
    if (v === '*') return `["${k}"]`;
    // v may be a pipe-separated list like "rail|light_rail"
    const escaped = v.replace(/"/g, '\\"');
    if (v.includes('|')) return `["${k}"~"${escaped}"]`;
    return `["${k}"="${escaped}"]`;
  }).join('');
}

function buildBboxString(f: FilterExpr & { kind: 'bbox' }): string {
  // Overpass bbox: (minLat,minLon,maxLat,maxLon) = (south,west,north,east)
  return `(${f.minLat},${f.minLon},${f.maxLat},${f.maxLon})`;
}

function buildNearString(f: FilterExpr & { kind: 'near' }): string {
  return `(around:${f.radiusMeters},${f.lat},${f.lon})`;
}

function filterExprToOverpassSuffix(filters: FilterExpr[]): string {
  const bbox = filters.find((f) => f.kind === 'bbox') as (FilterExpr & { kind: 'bbox' }) | undefined;
  const near = filters.find((f) => f.kind === 'near') as (FilterExpr & { kind: 'near' }) | undefined;
  // Also handle equality filters with variable 'bbox' or 'near' (shorthand syntax)
  const bboxEq = filters.find((f) => f.kind === 'equality' && (f.property === 'bbox' || f.variable === 'bbox'));
  const nearEq = filters.find((f) => f.kind === 'equality' && (f.property === 'near' || f.variable === 'near'));
  if (bbox) return buildBboxString(bbox);
  if (bboxEq && bboxEq.kind === 'equality') {
    // Parse bbox string value: "minLat,minLon,maxLat,maxLon"
    const parts = String(bboxEq.value).split(',').map(Number);
    if (parts.length === 4) return `(${parts[0]},${parts[1]},${parts[2]},${parts[3]})`;
  }
  if (near) return buildNearString(near);
  if (nearEq && nearEq.kind === 'equality') {
    // Parse near string value: "lat,lon,radiusMetres"
    const parts = String(nearEq.value).split(',').map(Number);
    if (parts.length === 3) return `(around:${parts[2]},${parts[0]},${parts[1]})`;
  }
  return '';
}

function buildNameFilter(value: string): string {
  // case-insensitive name contain filter for Overpass
  const escaped = value.replace(/"/g, '\\"');
  return `["name"~"${escaped}",i]`;
}

/** Build a union of node+way+relation for a given tag pattern and spatial suffix */
function buildUnion(
  tagFilter: string,
  spatialSuffix: string,
  out: 'center tags' | 'geom tags',
): string {
  return (
    OVERPASS_HEADER +
    '(\n' +
    `  node${tagFilter}${spatialSuffix};\n` +
    `  way${tagFilter}${spatialSuffix};\n` +
    `  relation${tagFilter}${spatialSuffix};\n` +
    ');\n' +
    `out ${out};`
  );
}

function buildWayOnly(
  tagFilter: string,
  spatialSuffix: string,
): string {
  return (
    OVERPASS_HEADER +
    `way${tagFilter}${spatialSuffix};\n` +
    'out geom tags;'
  );
}

// ---------------------------------------------------------------------------
// Main planner
// ---------------------------------------------------------------------------

export function plan(
  query: Query,
  registry: MappingRule[],
  unmapped: UnmappedRule[] = [],
): ExecutionPlan {
  const { eraClass, where, limit } = query;

  if (!eraClass) {
    return {
      kind: 'unsupported',
      reason: 'Could not determine ERA class from query. Use "?x a era:ClassName" in the WHERE clause.',
    };
  }

  // Find the mapping rule for this ERA class
  const eraEntityKey = `era:${eraClass}`;
  const rule = registry.find(
    (r) => r.eraEntity === eraEntityKey && r.eraKind === 'class',
  );

  if (!rule) {
    return {
      kind: 'unsupported',
      reason: `No mapping rule found for ${eraEntityKey}. Check mapping/era-to-osm.yaml.`,
    };
  }

  // Check for unmapped fields in the query's projected/filtered properties
  const queriedProperties = extractQueriedProperties(query);
  const unmappedFields = queriedProperties.filter((prop) =>
    unmapped.some((u) => u.eraEntity === `era:${prop}` || u.eraEntity === prop),
  );

  const filters = where.filters;

  // ---- Decision: ORM Facility Lookup ----
  if (eraClass === 'OperationalPoint') {
    const nameFilter = filters.find(
      (f) => (f.kind === 'contains' || f.kind === 'regex') &&
        (f.property === 'opName' || f.property === 'name'),
    );
    const uicFilter = filters.find(
      (f) => f.kind === 'equality' && (f.property === 'uicCode' || f.property === 'uic_ref' || f.property === 'uic'),
    );
    const refFilter = filters.find(
      (f) => f.kind === 'equality' && (f.property === 'opPrimaryLocationCode' || f.property === 'ref'),
    );

    const hasBboxOrNear = filters.some((f) =>
      f.kind === 'bbox' || f.kind === 'near' ||
      (f.kind === 'equality' && (f.property === 'bbox' || f.variable === 'bbox' ||
        f.property === 'near' || f.variable === 'near')),
    );

    if (!hasBboxOrNear) {
      if (uicFilter && uicFilter.kind === 'equality') {
        const facPlan: OrmFacilityLookup = {
          kind: 'orm-facility-lookup',
          searchTerm: String(uicFilter.value),
          searchType: 'uic',
          limit: limit?.value ?? 10,
          mappingRule: rule,
        };
        return wrapPartialIfNeeded(facPlan, unmappedFields);
      }
      if (refFilter && refFilter.kind === 'equality') {
        const facPlan: OrmFacilityLookup = {
          kind: 'orm-facility-lookup',
          searchTerm: String(refFilter.value),
          searchType: 'ref',
          limit: limit?.value ?? 10,
          mappingRule: rule,
        };
        return wrapPartialIfNeeded(facPlan, unmappedFields);
      }
      // Name-only queries go to Overpass with a name tag filter (ORM API lacks CORS headers)
      if (nameFilter) {
        const term = nameFilter.kind === 'contains' ? nameFilter.value
          : nameFilter.kind === 'regex' ? nameFilter.pattern
          : '';
        const tagF = eraClassToOsmFilter(eraClass, rule) + buildNameFilter(term);
        const ql = buildUnion(tagF, '', 'center tags');
        const overpassPlan: OverpassPrimary = {
          kind: 'overpass-primary',
          overpassQL: ql,
          eraClass,
          mappingRule: rule,
          postFilters: [],
        };
        return wrapPartialIfNeeded(overpassPlan, unmappedFields);
      }
    }
  }

  // ---- Decision: Overpass ----
  const tagFilter = eraClassToOsmFilter(eraClass, rule);
  const spatialSuffix = filterExprToOverpassSuffix(filters);

  // Block global queries — no spatial bound = certain Overpass timeout
  if (!spatialSuffix) {
    return {
      kind: 'unsupported',
      reason:
        `A global "${eraClass}" query (no FILTER with bbox or near) would time out on Overpass. ` +
        `Add a spatial constraint, e.g.: FILTER(?near = "49.0,8.4,5000") for a 5 km radius around lat 49.0 lon 8.4, ` +
        `or FILTER(?bbox = "48.9,8.3,49.1,8.6") for a bounding box.`,
    };
  }


  let extraTagFilter = tagFilter;
  if (eraClass === 'OperationalPoint') {
    const nameFilter = filters.find(
      (f) => (f.kind === 'contains' || f.kind === 'regex') &&
        (f.property === 'opName' || f.property === 'name'),
    );
    if (nameFilter) {
      const term = nameFilter.kind === 'contains' ? nameFilter.value
        : nameFilter.kind === 'regex' ? nameFilter.pattern : '';
      extraTagFilter = tagFilter + buildNameFilter(term);
    }
  }

  // Additional equality tag filters (e.g. electrified = true)
  for (const f of filters) {
    if (f.kind === 'equality') {
      const osmKey = eraPropertyToOsmTag(f.property);
      if (osmKey) {
        const osmVal = eraValueToOsmValue(f.property, f.value);
        extraTagFilter += `["${osmKey}"~"${osmVal}"]`;
      }
    }
  }

  // Determine output type (centre vs full geom)
  const needsGeom = eraClass !== 'OperationalPoint';
  const outClause = needsGeom ? 'geom tags' : 'center tags';

  // Choose union (node+way+rel) vs way-only
  const usesWayOnly = [
    'Track', 'RunningTrack', 'Tunnel', 'Bridge', 'ContactLineSystem', 'PlatformEdge',
  ].includes(eraClass);

  const overpassQL = usesWayOnly
    ? buildWayOnly(extraTagFilter, spatialSuffix)
    : buildUnion(extraTagFilter, spatialSuffix, outClause);

  // Post-filters for RunningTrack
  const postFilters: PostFilter[] = [];
  if (eraClass === 'RunningTrack') {
    postFilters.push({
      description: 'Exclude service tracks (sidings, spurs, yards)',
      excludeTagValues: {
        service: ['siding', 'spur', 'yard', 'crossover'],
      },
    });
  }

  if (unmappedFields.length > 0) {
    const warnings = unmappedFields.map(
      (f) => `Field "${f}" has no OSM equivalent and is omitted from results.`,
    );
    const base: OverpassPrimary = {
      kind: 'overpass-primary',
      overpassQL,
      eraClass,
      mappingRule: rule,
      postFilters,
    };
    return {
      kind: 'partial-translation',
      basePlan: base,
      unmappedFields,
      warnings,
    };
  }

  if (postFilters.length > 0) {
    return {
      kind: 'overpass-plus-postfilter',
      overpassQL,
      eraClass,
      mappingRule: rule,
      postFilters,
      warnings: [],
    };
  }

  return {
    kind: 'overpass-primary',
    overpassQL,
    eraClass,
    mappingRule: rule,
    postFilters,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function wrapPartialIfNeeded(
  basePlan: OrmFacilityLookup | OverpassPrimary,
  unmappedFields: string[],
): ExecutionPlan {
  if (unmappedFields.length === 0) return basePlan;
  return {
    kind: 'partial-translation',
    basePlan,
    unmappedFields,
    warnings: unmappedFields.map(
      (f) => `Field "${f}" has no OSM/ORM equivalent and is omitted from results.`,
    ),
  };
}

function extractQueriedProperties(query: Query): string[] {
  const props: string[] = [];
  for (const tp of query.where.patterns) {
    if (tp.predicate !== 'a' && tp.predicate !== 'rdf:type') {
      const localName = tp.predicate.includes(':') ? tp.predicate.split(':')[1]! : tp.predicate;
      props.push(localName);
    }
  }
  for (const f of query.where.filters) {
    if ('property' in f && f.property) props.push(f.property);
  }
  return [...new Set(props)];
}

/** Map ERA property names to OSM tag keys */
function eraPropertyToOsmTag(property: string): string | null {
  const map: Record<string, string> = {
    electrified: 'electrified',
    contactLineSystemType: 'electrified',
    voltage: 'voltage',
    frequency: 'frequency',
    maximumPermittedSpeed: 'maxspeed',
    gauge: 'gauge',
    trackGauge: 'gauge',
    tunnel: 'tunnel',
    bridge: 'bridge',
  };
  return map[property] ?? null;
}

/** Map ERA property values to OSM tag values */
function eraValueToOsmValue(property: string, value: string | number | boolean): string {
  if (property === 'electrified' && value === true) {
    return 'yes|contact_line|rail|ground-level_power_supply';
  }
  if (property === 'electrified' && value === false) return 'no';
  if (property === 'tunnel' && value === true) return 'yes';
  if (property === 'bridge' && value === true) return 'yes|viaduct|movable';
  return String(value);
}
